import { useQuery } from "@tanstack/react-query";
import axios from "axios";

// One reference's trade quotes, as feed cards — /api/core-list/{item}/matches.
//
// Its own endpoint, and its own hook, because the list is unbounded: one
// reference drew 945 matches in production. /api/core-list/targets carries
// only the COUNT, and nothing fetches the rows until someone opens the match
// view for that one reference.
const fetchMatches = async (itemId, filters) => {
  const { data } = await axios.get(`/api/core-list/${itemId}/matches`, {
    params: {
      bucket: filters.bucket,
      // Sent only when on: a `false` in the query string is still a value the
      // validator has to accept, and "no filter" is the honest absence.
      ...(filters.underpriced ? { underpriced: 1 } : {}),
      ...(filters.band ? { band: filters.band } : {}),
      ...(filters.q ? { q: filters.q } : {}),
      // A region is sent by KEY and expanded server-side: the client learns
      // which regions exist from the facets, but what a region contains is
      // config the server owns. Sending one clears the other, so the two can
      // never narrow at once and produce an empty result nobody asked for.
      ...(filters.country ? { countries: [filters.country] } : {}),
      ...(filters.region ? { region: filters.region } : {}),
      ...(filters.period ? { period: filters.period } : {}),
      // Hidden here, for this look, ON TOP OF whatever the store has muted on
      // Trade Feed. Deliberately per-request rather than editing the shared
      // "Hide groups" setting: muting a group while reading one reference's
      // quotes should not silently change what Trade Feed shows everyone.
      ...(filters.hiddenGroups?.length ? { exclude_groups: filters.hiddenGroups } : {}),
      ...(filters.mediaAndText ? { media_and_text: 1 } : {}),
      ...(filters.hideBulk ? { hide_bulk: 1 } : {}),
      page: filters.page,
      per_page: filters.perPage,
    },
    // NO paramsSerializer. This repo is on axios 0.19, where that option must
    // be a FUNCTION — the `{ indexes: null }` object form is axios 1.x, and
    // passing it threw "paramsSerializer is not a function" on every request,
    // so the match view loaded nothing at all. Caught by the suite below.
    //
    // It is not needed either: axios 0.x already serialises an array as
    // `countries[]=hk&countries[]=cn` (buildURL appends `[]` for arrays),
    // which is exactly the shape Laravel parses back into an array.
  });
  return data;
};

/**
 * @param {number|null} itemId   core_list_item_id, null when closed
 * @param {object} filters       bucket, underpriced, band, q, country, region,
 *                               period, hiddenGroups, mediaAndText, hideBulk,
 *                               page, perPage
 */
export const useCoreListMatches = (itemId, filters) => {
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["core-list-matches", itemId, filters],
    queryFn: () => fetchMatches(itemId, filters),
    enabled: itemId != null,
    // Changing a filter keeps the previous page on screen while the next one
    // loads. Without it every keystroke in the keyword box empties the grid
    // and the whole view jumps back to the top. (react-query v4 spelling —
    // v5's placeholderData/keepPreviousData import does not exist here.)
    keepPreviousData: true,
  });

  return {
    items: data?.items || [],
    total: Number(data?.total) || 0,
    counts: data?.counts || { at_target: 0, unknown_year: 0, above: 0 },
    bands: data?.bands || [],
    medianBase: data?.median_base ?? null,
    baseCurrency: data?.base_currency || "gbp",
    // Which countries and groups these quotes actually came from. Served with
    // the matches rather than fetched from /api/trade-feed/countries, which is
    // master-admin only — Core List is a store-scoped page.
    facetCountries: data?.facets?.countries || [],
    facetGroups: data?.facets?.groups || [],
    // The catalogue photo, used only where the message itself carried none.
    productImageFileId: data?.product_image_file_id ?? null,
    // isFetching, not isLoading: with keepPreviousData the initial flag is only
    // true on the very first load, and the grid still needs to say it is busy
    // when a filter changes.
    isLoading,
    isFetching,
    isError,
  };
};

export default useCoreListMatches;
